<template>
  <div
    class="home-page"
    :style="{
      '--level-bg': 'url(' + levelBg + ')',
      '--card-bg': 'url(' + cardBg + ')',
    }"
  >
    <header
      class="cockpit-header"
      :style="{ backgroundImage: 'url(' + topBg + ')' }"
    >
      <!-- <div class="header-side">
        <span class="live-indicator"></span>
      </div> -->
      <h1>广告收益数据大屏</h1>
      <div class="cockpit-clock">
        <button
          type="button"
          class="fullscreen-btn"
          :title="isFullscreen ? '退出全屏' : '全屏'"
          @click="toggleFullscreen"
        >
          <svg v-if="!isFullscreen" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>{{ isFullscreen ? "退出全屏" : "全屏" }}</span>
        </button>
        <div class="range-switch">
          <button
            v-for="item in trendRanges"
            :key="item.key"
            type="button"
            :class="{ active: dateFilter.key === item.key }"
            @click="setTrendRange(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <span>{{ systemDate }}</span>
        <strong>{{ systemTime }}</strong>
      </div>
    </header>
    <main class="cockpit-grid">
      <div class="side-stack left-stack">
        <section class="dash-card card-channel">
          <h3 class="card-title">广告渠道收益占比</h3>
          <div class="card-body channel-body">
            <div class="channel-main">
              <ul class="channel-legend">
                <li
                  v-for="item in channelItems"
                  :key="item.adnName"
                  class="channel-legend-item"
                >
                  <span class="dot" :style="{ background: item.color }"></span
                  ><span class="name">{{ item.adnName }}</span
                  ><span class="pct">{{ formatPercent(item.percent) }}%</span>
                </li>
              </ul>
              <div ref="channelChart" class="channel-chart"></div>
            </div>
          </div>
        </section>
        <section class="dash-card card-ecpm">
          <h3 class="card-title">渠道 eCPM 对比</h3>
          <div class="card-body ecpm-body">
            <div
              v-for="item in channelEcpmBars"
              :key="item.adnName"
              class="ecpm-row"
            >
              <span class="ecpm-name">{{ item.adnName }}</span>
              <div class="ecpm-track">
                <div
                  class="ecpm-fill"
                  :style="{
                    width: item.barWidth,
                    background: item.gradient,
                    color: item.color,
                  }"
                ></div>
              </div>
              <span class="ecpm-val">{{ item.display }}</span>
            </div>
          </div>
        </section>
        <section class="dash-card card-metrics">
          <h3 class="card-title">广告核心指标</h3>
          <div class="card-body metric-grid">
            <section
              v-for="card in metricCards"
              :key="card.key"
              class="metric-cube"
              :class="'is-' + card.theme"
            >
              <div class="metric-cube-value">
                <strong>{{ card.display }}</strong
                ><small v-if="card.unit">{{ card.unit }}</small>
              </div>
              <div class="metric-cube-label">{{ card.label }}</div>
              <div class="metric-cube-stage">
                <div v-show="!kpiArtReady[card.key]" class="metric-cube-css">
                  <i class="face top"></i>
                  <i class="face left"></i>
                  <i class="face right"></i>
                </div>
                <img
                  class="metric-cube-img"
                  :class="{ ready: kpiArtReady[card.key] }"
                  :src="card.art"
                  alt=""
                  @load="onKpiArtLoad(card.key)"
                  @error="onKpiArtError(card.key)"
                />
              </div>
            </section>
          </div>
        </section>
      </div>
      <section
        class="dash-card map-card"
        :class="{ 'is-map-zoomed': mapZoomed }"
        :style="{
          '--map-area-bg': 'url(' + mapAreaBg + ')',
          '--map-area-bg2': 'url(' + mapAreaBg2 + ')',
        }"
      >
        <div class="map-kpis">
          <div class="map-kpi">
            <span>{{ totalUsersLabel }}</span
            ><strong>{{ formatCount(activeUsersTotal) }}</strong
            ><small>{{ usersEnLabel }}</small>
          </div>
          <div class="map-kpi revenue">
            <span>{{ totalRevenueLabel }}</span
            ><strong>¥{{ formatRevenue(totalRevenue) }}</strong
            ><small>{{ revenueEnLabel }}</small>
          </div>
          <div class="map-kpi online">
            <span>实时在线</span><strong>{{ onlineCountDisplay }}</strong
            ><small>ONLINE NOW</small>
          </div>
        </div>
        <div class="china-map-wrap">
          <div class="province-map-layer">
            <div class="map-bg2-layer" aria-hidden="true"></div>
            <china-province-map
              :provinces="provincePoints"
              :count-formatter="formatCount"
              :revenue-formatter="formatRevenue"
              @zoom-change="mapZoomed = $event"
            />
          </div>
        </div>
        <section class="dash-card metric-panel revenue-panel map-revenue-panel">
          <div class="map-revenue-head">
            <h3 class="card-title">{{ trendChartTitle }}</h3>
            <div class="trend-legend">
              <span class="trend-legend-item is-revenue"><i></i>预估收益</span>
              <span class="trend-legend-item is-impression"
                ><i></i>有效曝光</span
              >
            </div>
          </div>
          <div class="card-body">
            <div ref="trendChart" class="trend-chart"></div>
          </div>
        </section>
      </section>
      <div class="side-stack right-stack">
        <section class="dash-card card-warn">
          <div class="risk-header">
            <h3 class="card-title">风控中心</h3>
            <span class="risk-total"
              >{{ riskRatioLabel }} <b>{{ formatPercent(riskRatio) }}%</b></span
            >
          </div>
          <div class="risk-mini-stats">
            <span
              >{{ riskDeviceLabel }} <b>{{ riskDeviceCount }}</b></span
            ><span
              >{{ aliyunDetectLabel }} <b>{{ aliyunDetectCount }}</b></span
            >
          </div>
          <div class="card-body risk-body">
            <div class="risk-radar-pane">
              <div ref="riskRadarChart" class="risk-radar-chart"></div>
            </div>
            <div class="risk-table-pane">
              <div v-if="!riskRecords.length" class="risk-empty">
                暂无风控记录
              </div>
              <template v-else>
                <table class="risk-table risk-table-head">
                  <thead>
                    <tr>
                      <th>用户</th>
                      <th>风险类型</th>
                      <th>原因</th>
                      <th>状态</th>
                    </tr>
                  </thead>
                </table>
                <div class="risk-scroll-viewport">
                  <div
                    class="risk-scroll-track"
                    :class="{ 'is-scrolling': riskNeedScroll }"
                    :style="riskScrollStyle"
                  >
                    <table class="risk-table risk-table-body">
                      <tbody>
                        <tr
                          v-for="(item, index) in riskDisplayList"
                          :key="item._key || item.id || index"
                        >
                          <td :title="item.phone || item.openid">
                            {{
                              item.phone ||
                              item.nickname ||
                              shortId(item.openid)
                            }}
                          </td>
                          <td>{{ riskTypeMainLabel(item) }}</td>
                          <td :title="item.reason">{{ item.reason || "-" }}</td>
                          <td>{{ item.userStatusLabel || "-" }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </section>
        <section class="dash-card month-panel">
          <h3 class="card-title">近 6 个月新增用户</h3>
          <div class="card-body">
            <div ref="monthChart" class="month-chart"></div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { get } from "@/utils/http";
import {
  getAdPlatformSummary,
  getHourlyTrend,
  getRiskCenter,
  getTakuOverview,
  getUserLocations,
} from "@/api/dashboard";
import { getTodayRange, DATE_RANGE_PRESETS } from "@/utils/dateRange";
import { ecpmToYuan } from "@/utils/coinMoney";
import ChinaProvinceMap from "@/components/ChinaProvinceMap.vue";

const PROVINCE_POINTS = [
  // Percent of the 2511 × 1632 China map (public/ditu.png).
  ["新疆", 20.8, 35.5],
  ["西藏", 27.2, 63.2],
  ["青海", 39.8, 48.0],
  ["甘肃", 47.2, 44.2],
  ["宁夏", 52.6, 45.0],
  ["内蒙古", 58.0, 36.4],
  ["黑龙江", 85.4, 17.6],
  ["吉林", 82.8, 27.6],
  ["辽宁", 79.0, 35.6],
  ["北京", 70.0, 37.2],
  ["天津", 72.6, 40.4],
  ["河北", 67.8, 42.2],
  ["山西", 62.4, 44.6],
  ["陕西", 59.0, 51.6],
  ["山东", 71.8, 48.0],
  ["河南", 64.8, 54.0],
  ["江苏", 75.2, 57.2],
  ["安徽", 70.4, 59.4],
  ["上海", 78.6, 61.4],
  ["湖北", 62.0, 62.2],
  ["浙江", 76.2, 66.4],
  ["重庆", 55.4, 65.2],
  ["四川", 46.4, 63.0],
  ["湖南", 62.2, 70.8],
  ["江西", 70.0, 70.6],
  ["福建", 75.4, 75.6],
  ["贵州", 54.0, 73.6],
  ["云南", 44.6, 77.4],
  ["广东", 66.5, 82.0],
  ["广西", 56.0, 82.4],
  ["海南", 59.0, 94.2],
  ["台湾", 77.6, 81.8],
  ["香港", 70.0, 90.8],
  ["澳门", 65.8, 92.2],
];

/** 按收益从大到小：蓝、绿、黄、紫 */
const RANK_COLORS = ["#2B6DE5", "#22C55E", "#EAB308", "#8B5CF6"];
const RANK_GRADIENTS = [
  "linear-gradient(90deg, #163fad 0%, #2B6DE5 42%, #7eb6ff 100%)",
  "linear-gradient(90deg, #15803d 0%, #22C55E 42%, #86efac 100%)",
  "linear-gradient(90deg, #a16207 0%, #EAB308 42%, #fde68a 100%)",
  "linear-gradient(90deg, #5b21b6 0%, #8B5CF6 42%, #d8b4fe 100%)",
];
const CHANNEL_ORDER = ["穿山甲", "快手", "腾讯广告", "百度联盟"];
const FALLBACK_COLORS = [
  "#2B6DE5",
  "#22C55E",
  "#EAB308",
  "#8B5CF6",
  "#38BDF8",
  "#A3E635",
];
const FALLBACK_GRADIENTS = [
  "linear-gradient(90deg, #163fad 0%, #2B6DE5 42%, #7eb6ff 100%)",
  "linear-gradient(90deg, #15803d 0%, #22C55E 42%, #86efac 100%)",
  "linear-gradient(90deg, #a16207 0%, #EAB308 42%, #fde68a 100%)",
  "linear-gradient(90deg, #5b21b6 0%, #8B5CF6 42%, #d8b4fe 100%)",
  "linear-gradient(90deg, #0369a1 0%, #38BDF8 42%, #bae6fd 100%)",
  "linear-gradient(90deg, #4d7c0f 0%, #A3E635 42%, #d9f99d 100%)",
];

function assignChannelColors(items) {
  const ranked = items
    .map((item, index) => ({ index, value: Number(item.ecpm) || 0 }))
    .sort((a, b) => b.value - a.value || a.index - b.index);
  const colorByIndex = {};
  const gradientByIndex = {};
  ranked.forEach((row, rank) => {
    colorByIndex[row.index] =
      RANK_COLORS[rank] || FALLBACK_COLORS[rank % FALLBACK_COLORS.length];
    gradientByIndex[row.index] =
      RANK_GRADIENTS[rank] ||
      FALLBACK_GRADIENTS[rank % FALLBACK_GRADIENTS.length];
  });
  return items.map((item, index) => ({
    ...item,
    color: colorByIndex[index],
    gradient: gradientByIndex[index],
  }));
}
const RANGE_LABELS = {
  today: "今日",
  day: "今日",
  month: "本月",
  year: "今年",
  yesterday: "昨日",
  last7: "近七日",
};
/** 雷达轴固定顺序，与后端 typeStats 对齐 */
const RISK_TYPE_DEFS = [
  { riskType: "ALIYUN", label: "风险检测" },
  { riskType: "BAN", label: "封禁" },
  { riskType: "IP_DEVICE", label: "同IP设备" },
  { riskType: "WARN_DEVICE", label: "风险设备" },
  { riskType: "BLOCK_DEVICE", label: "风控设备" },
];

export default {
  name: "HomePage",
  components: { ChinaProvinceMap },
  data() {
    return {
      dateFilter: {
        key: "today",
        range: getTodayRange(),
      },
      channelItems: assignChannelColors(
        CHANNEL_ORDER.map((name) => ({
          adnName: name,
          percent: 0,
          ecpm: 0,
        }))
      ),
      channelChart: null,
      trendChart: null,
      monthChart: null,
      monthlyNewUsers: [],
      hourlyTrendItems: [],
      riskDeviceCount: 0,
      warnDeviceCount: 0,
      aliyunDetectCount: 0,
      riskRatio: 0,
      riskTypeStats: [],
      riskRecords: [],
      riskRadarChart: null,
      totalRevenue: 0,
      todayRevenue: 0,
      /** 翻牌刷新计数：数值未变时也轻跳 */
      revenueOdometerTick: 0,
      onlineCount: 0,
      onlineRefreshTimer: null,
      /** 仅「今日」时对走库表卡片 5s 轮询 */
      dbRefreshTimer: null,
      dbRefreshInFlight: false,
      /** 昨日 / 近七日按筛选键缓存，历史数据不再重复请求 */
      historyCache: {},
      takuOverview: {
        estimateRevenue: 0,
        revenue: 0,
        impression: 0,
        request: 0,
        fillRate: 0,
        ecpm: 0,
        dau: 0,
      },
      systemDate: "",
      systemTime: "",
      clockTimer: null,
      kpiArtReady: {},
      todayUsers: 0,
      isFullscreen: false,
      mapZoomed: false,
      provincePoints: PROVINCE_POINTS.map(([name, x, y]) => ({
        name,
        x,
        y,
        userCount: 0,
        revenue: 0,
      })),
      trendRanges: DATE_RANGE_PRESETS.map((item) => ({
        key: item.key,
        label: item.label,
      })),
    };
  },
  computed: {
    dateRange() {
      const range = this.dateFilter && this.dateFilter.range;
      if (Array.isArray(range) && range.length === 2) return range;
      return getTodayRange();
    },
    mapAreaBg() {
      return `${process.env.BASE_URL || "/"}ditubg.png`;
    },
    mapAreaBg2() {
      return `${process.env.BASE_URL || "/"}ditu_bg2.png`;
    },
    topBg() {
      return `${process.env.BASE_URL || "/"}top.png`;
    },
    levelBg() {
      return `${process.env.BASE_URL || "/"}level.png`;
    },
    cardBg() {
      return `${process.env.BASE_URL || "/"}level_bg.png`;
    },
    dateFilterKey() {
      return (this.dateFilter && this.dateFilter.key) || "today";
    },
    dateRangeLabel() {
      return RANGE_LABELS[this.dateFilterKey] || "今日";
    },
    estimateRevenueLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日预估收益";
      if (key === "last7") return "近七日预估收益";
      return "今日预估收益";
    },
    revenueLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日收益";
      if (key === "last7") return "近七日收益";
      return "今日收益";
    },
    totalRevenueLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日总收益";
      if (key === "last7") return "近七日总收益";
      return "今日总收益";
    },
    totalUsersLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日总用户";
      if (key === "last7") return "近七日总用户";
      return "今日总用户";
    },
    usersEnLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "USERS YESTERDAY";
      if (key === "last7") return "USERS 7 DAYS";
      return "USERS TODAY";
    },
    revenueEnLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "REVENUE YESTERDAY";
      if (key === "last7") return "REVENUE 7 DAYS";
      return "REVENUE TODAY";
    },
    trendChartTitle() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日收益趋势";
      if (key === "last7") return "近七日收益趋势";
      return "今日实时总收益";
    },
    riskDeviceLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日风控设备";
      if (key === "last7") return "近七日风控设备";
      return "今日风控设备";
    },
    warnDeviceLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日风险设备";
      if (key === "last7") return "近七日风险设备";
      return "今日风险设备";
    },
    aliyunDetectLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日风险检测";
      if (key === "last7") return "近七日风险检测";
      return "今日风险检测";
    },
    riskRatioLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日风险概率";
      if (key === "last7") return "近七日风险概率";
      return "今日风险概率";
    },
    dauLabel() {
      const key = this.dateFilterKey;
      if (key === "yesterday") return "昨日活跃";
      if (key === "last7") return "近七日活跃";
      return "DAU";
    },
    /** 区间活跃用户：走 takuOverview.dau（ad_record 去重），不依赖 IP 归属地 */
    activeUsersTotal() {
      return Number((this.takuOverview && this.takuOverview.dau) || 0);
    },
    metricCards() {
      const o = this.takuOverview || {};
      const base = process.env.BASE_URL || "/";
      return [
        {
          key: "estimateRevenue",
          theme: "orange",
          label: this.estimateRevenueLabel,
          display: this.formatMoney(o.estimateRevenue),
          unit: "元",
          art: `${base}kpi-orange.png`,
        },
        {
          key: "revenue",
          theme: "gold",
          label: this.revenueLabel,
          display: this.formatMoney(o.revenue),
          unit: "元",
          art: `${base}kpi-gold.png`,
        },
        {
          key: "request",
          theme: "green",
          label: "广告请求",
          display: this.formatCount(o.request),
          art: `${base}kpi-green.png`,
        },
        {
          key: "fillRate",
          theme: "purple",
          label: "填充率",
          display: this.formatFillRate(o.fillRate),
          art: `${base}kpi-purple.png`,
        },
        {
          key: "ecpm",
          theme: "cyan",
          label: "eCPM",
          display: this.formatMoney(o.ecpm),
          unit: "元",
          art: `${base}kpi-cyan.png`,
        },
        {
          key: "dau",
          theme: "blue",
          label: this.dauLabel,
          display: this.formatCount(o.dau),
          art: `${base}kpi-blue.png`,
        },
      ];
    },
    channelEcpmBars() {
      const items = this.channelItems || [];
      const max = Math.max(...items.map((i) => Number(i.ecpm) || 0), 0);
      return items.map((item) => {
        const n = Number(item.ecpm) || 0;
        return {
          adnName: item.adnName,
          color: item.color,
          gradient: item.gradient,
          display: this.formatRevenue(n),
          barWidth:
            max > 0 ? `${Math.max((n / max) * 100, n > 0 ? 6 : 0)}%` : "0%",
        };
      });
    },
    totalRevenueYuan() {
      return ecpmToYuan(this.totalRevenue);
    },
    onlineCountDisplay() {
      const num = Math.round(Number(this.onlineCount) || 0);
      if (num >= 10000) return `${(num / 10000).toFixed(2)}万`;
      return num.toLocaleString("zh-CN");
    },
    /** 超过 4 条开启无缝上下滚动 */
    riskNeedScroll() {
      return (this.riskRecords || []).length > 4;
    },
    riskDisplayList() {
      const list = (this.riskRecords || []).map((item, index) => ({
        ...item,
        _key: `a-${item.id || index}`,
      }));
      if (!this.riskNeedScroll) return list;
      return list.concat(
        (this.riskRecords || []).map((item, index) => ({
          ...item,
          _key: `b-${item.id || index}`,
        }))
      );
    },
    riskScrollStyle() {
      if (!this.riskNeedScroll) return {};
      const sec = Math.max(12, (this.riskRecords || []).length * 2.4);
      return { animationDuration: `${sec}s` };
    },
  },
  watch: {
    "dateFilter.key"() {
      this.reloadDateBoundCards();
      this.syncDbRefreshTimer();
    },
  },
  mounted() {
    this.tickSystemClock();
    this.clockTimer = setInterval(this.tickSystemClock, 1000);
    this.$nextTick(() => {
      this.initChannelChart();
      this.initTrendChart();
      this.initMonthChart();
      this.initRiskRadarChart();
      this.reloadDateBoundCards();
      this.loadMonthlyNewUsers();
      this.loadTodayRevenue();
      this.loadOnlineUsers();
      this.onlineRefreshTimer = setInterval(
        () => this.loadOnlineUsers(),
        20000
      );
      this.syncDbRefreshTimer();
      this.$nextTick(() => this.handleResize());
    });
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("fullscreenchange", this.syncFullscreen);
    document.addEventListener("webkitfullscreenchange", this.syncFullscreen);
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("fullscreenchange", this.syncFullscreen);
    document.removeEventListener("webkitfullscreenchange", this.syncFullscreen);
    this.stopDbRefreshTimer();
    if (this.onlineRefreshTimer) {
      clearInterval(this.onlineRefreshTimer);
      this.onlineRefreshTimer = null;
    }
    if (this.channelChart) {
      this.channelChart.dispose();
      this.channelChart = null;
    }
    if (this.trendChart) {
      this.trendChart.dispose();
      this.trendChart = null;
    }
    if (this.monthChart) {
      this.monthChart.dispose();
      this.monthChart = null;
    }
    if (this.riskRadarChart) {
      this.riskRadarChart.dispose();
      this.riskRadarChart = null;
    }
  },
  methods: {
    setTrendRange(key) {
      if (!this.dateFilter || this.dateFilter.key === key) return;
      const preset = DATE_RANGE_PRESETS.find((item) => item.key === key);
      if (!preset) return;
      this.dateFilter.key = key;
      this.dateFilter.range = preset.getRange();
    },
    tickSystemClock() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      this.systemDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}`;
      this.systemTime = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
        now.getSeconds()
      )}`;
    },
    async loadTodayRevenue() {
      try {
        const [startTime, endTime] = getTodayRange();
        const summary = await getAdPlatformSummary(startTime, endTime);
        this.todayRevenue = Number(summary.totalRevenue) || 0;
      } catch (e) {
        this.todayRevenue = 0;
      }
    },
    onKpiArtLoad(key) {
      this.$set(this.kpiArtReady, key, true);
    },
    onKpiArtError(key) {
      this.$set(this.kpiArtReady, key, false);
    },
    formatPercent(value) {
      const n = Number(value) || 0;
      return n >= 10 || n === 0 ? Math.round(n) : n.toFixed(1);
    },
    formatMoney(value) {
      const n = Number(value) || 0;
      if (n >= 10000) return n.toFixed(0);
      if (n >= 100) return n.toFixed(1);
      if (n >= 1) return n.toFixed(2);
      return n.toFixed(3);
    },
    formatRevenue(value) {
      return this.formatMoney(ecpmToYuan(value || 0));
    },
    formatCount(value) {
      const n = Math.round(Number(value) || 0);
      return n.toLocaleString("zh-CN");
    },
    formatFillRate(value) {
      const n = Number(value) || 0;
      return `${n.toFixed(n >= 10 ? 1 : 2)}%`;
    },
    shortId(value) {
      const s = String(value || "");
      if (!s) return "-";
      return s.length > 10 ? `${s.slice(0, 4)}…${s.slice(-4)}` : s;
    },
    /** 只展示一级类型文案（如「封禁」），去掉 /ADMIN 等子标签 */
    riskTypeMainLabel(item) {
      const label = String(
        (item && (item.riskTypeLabel || item.riskType)) || ""
      );
      if (!label) return "-";
      const slash = label.indexOf("/");
      return slash >= 0 ? label.slice(0, slash) : label;
    },
    handleResize() {
      if (this.channelChart) this.channelChart.resize();
      if (this.trendChart) this.trendChart.resize();
      if (this.monthChart) this.monthChart.resize();
      if (this.riskRadarChart) this.riskRadarChart.resize();
    },
    peekHistory(field, filterKey = this.dateFilterKey) {
      if (["today", "day"].includes(filterKey)) return undefined;
      const pack = this.historyCache[filterKey];
      if (!pack || !Object.prototype.hasOwnProperty.call(pack, field)) {
        return undefined;
      }
      return pack[field];
    },
    putHistory(field, value, filterKey = this.dateFilterKey) {
      if (["today", "day"].includes(filterKey)) return;
      if (!this.historyCache[filterKey]) {
        this.$set(this.historyCache, filterKey, {});
      }
      this.$set(this.historyCache[filterKey], field, value);
    },
    stillOn(filterKey) {
      return this.dateFilterKey === filterKey;
    },
    fullscreenElement() {
      return document.fullscreenElement || document.webkitFullscreenElement;
    },
    syncFullscreen() {
      this.isFullscreen = this.fullscreenElement() === this.$el;
      this.$nextTick(() => this.handleResize());
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) exit.call(document);
        return;
      }
      const el = this.$el;
      const enter = el.requestFullscreen || el.webkitRequestFullscreen;
      if (enter) enter.call(el);
    },
    /** 跟随顶栏日筛选的卡片；近6个月新增用户、实时在线不在此列 */
    reloadDateBoundCards() {
      this.loadChannelRevenue();
      this.loadTakuOverview();
      this.loadHourlyTrend();
      this.loadRiskCenter();
      this.loadUserLocations();
    },
    isTodayFilter() {
      return ["today", "day"].includes(
        (this.dateFilter && this.dateFilter.key) || "today"
      );
    },
    /** 仅今日启动库表卡片 5s 轮询；昨日/近七日关闭。Taku 不参与轮询。 */
    syncDbRefreshTimer() {
      if (this.isTodayFilter()) {
        this.startDbRefreshTimer();
      } else {
        this.stopDbRefreshTimer();
      }
    },
    startDbRefreshTimer() {
      this.stopDbRefreshTimer();
      this.dbRefreshTimer = setInterval(() => this.refreshDbCards(), 5000);
    },
    stopDbRefreshTimer() {
      if (this.dbRefreshTimer) {
        clearInterval(this.dbRefreshTimer);
        this.dbRefreshTimer = null;
      }
    },
    /** 走库表卡片静默刷新；不含 Taku 核心指标 / 趋势 */
    async refreshDbCards() {
      if (!this.isTodayFilter() || this.dbRefreshInFlight) return;
      this.dbRefreshInFlight = true;
      try {
        await Promise.all([
          this.loadChannelRevenue(),
          this.loadRiskCenter(),
          this.loadMonthlyNewUsers(),
          this.loadTodayRevenue(),
          this.loadUserLocations(),
        ]);
      } finally {
        this.dbRefreshInFlight = false;
      }
    },
    async loadTakuOverview() {
      const filterKey = this.dateFilterKey;
      const cached = this.peekHistory("takuOverview", filterKey);
      if (cached !== undefined) {
        this.takuOverview = cached;
        return;
      }
      try {
        const [startTime, endTime] = this.dateRange;
        const data = await getTakuOverview(startTime, endTime);
        if (!this.stillOn(filterKey)) return;
        this.takuOverview = data;
      } catch (e) {
        if (!this.stillOn(filterKey)) return;
        this.takuOverview = {
          estimateRevenue: 0,
          revenue: 0,
          impression: 0,
          request: 0,
          fillRate: 0,
          ecpm: 0,
          dau: 0,
        };
      }
      this.putHistory("takuOverview", this.takuOverview, filterKey);
    },
    initChannelChart() {
      if (!this.$refs.channelChart) return;
      this.channelChart = echarts.init(this.$refs.channelChart);
      this.renderChannelChart();
    },
    initTrendChart() {
      if (!this.$refs.trendChart) return;
      this.trendChart = echarts.init(this.$refs.trendChart);
      this.renderTrendChart();
    },
    initMonthChart() {
      if (!this.$refs.monthChart) return;
      this.monthChart = echarts.init(this.$refs.monthChart);
      this.renderMonthChart();
    },
    initRiskRadarChart() {
      if (!this.$refs.riskRadarChart) return;
      this.riskRadarChart = echarts.init(this.$refs.riskRadarChart);
      this.renderRiskRadarChart();
    },
    renderRiskRadarChart() {
      if (!this.riskRadarChart) return;
      const countByType = {};
      (this.riskTypeStats || []).forEach((item) => {
        if (!item || !item.riskType) return;
        countByType[item.riskType] = Number(item.count) || 0;
      });
      const labels = RISK_TYPE_DEFS.map((d) => d.label);
      const values = RISK_TYPE_DEFS.map((d) => countByType[d.riskType] || 0);
      const maxVal = Math.max(...values, 0);
      const indicatorMax = maxVal > 0 ? Math.ceil(maxVal * 1.2) : 1;
      this.riskRadarChart.setOption(
        {
          radar: {
            center: ["50%", "50%"],
            radius: "56%",
            splitNumber: 4,
            axisNameGap: 4,
            axisName: {
              color: "#8db9d5",
              fontSize: 11,
              fontWeight: 600,
              overflow: "none",
              fontFamily: '"Microsoft YaHei", "PingFang SC", sans-serif',
            },
            axisLine: { lineStyle: { color: "rgba(58, 132, 181, 0.42)" } },
            splitLine: { lineStyle: { color: "rgba(58, 132, 181, 0.42)" } },
            splitArea: {
              show: true,
              areaStyle: {
                color: ["rgba(16,55,87,0.4)", "rgba(10,37,62,0.3)"],
              },
            },
            indicator: labels.map((name) => ({
              name:
                name === "同IP设备"
                  ? "同IP\n设备"
                  : name === "风险设备"
                  ? "风险\n设备"
                  : name === "风控设备"
                  ? "风控\n设备"
                  : name === "风险检测"
                  ? "风险\n检测"
                  : name,
              max: indicatorMax,
            })),
          },
          series: [
            {
              type: "radar",
              data: [
                {
                  value: values,
                  name: "风控次数",
                  symbol: "circle",
                  symbolSize: 4,
                  lineStyle: { width: 2, color: "#3B82F6" },
                  itemStyle: { color: "#3B82F6" },
                  areaStyle: { color: "rgba(59, 130, 246, 0.28)" },
                },
              ],
            },
          ],
        },
        true
      );
    },
    async loadHourlyTrend() {
      const filterKey = this.dateFilterKey;
      const cached = this.peekHistory("hourlyTrendItems", filterKey);
      if (cached !== undefined) {
        this.hourlyTrendItems = cached;
        this.renderTrendChart();
        return;
      }
      try {
        const [startTime, endTime] = this.dateRange;
        const items = await getHourlyTrend(startTime, endTime);
        if (!this.stillOn(filterKey)) return;
        this.hourlyTrendItems = items;
      } catch (e) {
        if (!this.stillOn(filterKey)) return;
        this.hourlyTrendItems = [];
      }
      this.putHistory("hourlyTrendItems", this.hourlyTrendItems, filterKey);
      this.renderTrendChart();
    },
    renderTrendChart() {
      if (!this.trendChart) return;
      const items = this.hourlyTrendItems || [];
      const hasData = items.some(
        (i) =>
          (Number(i.estimateRevenue) || 0) > 0 ||
          (Number(i.impression) || 0) > 0
      );
      const xData = items.length
        ? items.map((i) => i.hour)
        : Array.from(
            { length: 24 },
            (_, h) => `${String(h).padStart(2, "0")}:00`
          );
      const revenueData = items.length
        ? items.map((i) => Number(i.estimateRevenue) || 0)
        : xData.map(() => 0);
      const impressionData = items.length
        ? items.map((i) => Number(i.impression) || 0)
        : xData.map(() => 0);
      const labelInterval =
        xData.length > 48
          ? Math.ceil(xData.length / 12) - 1
          : xData.length > 24
          ? 5
          : 2;

      const chartFont = {
        fontFamily: '"SimHei", "Microsoft YaHei", "PingFang SC", sans-serif',
        fontWeight: "bold",
        color: "#86b5d2",
        textShadowColor: "rgba(0, 12, 24, 0.8)",
        textShadowBlur: 4,
        textShadowOffsetX: 0,
        textShadowOffsetY: 0,
      };

      this.trendChart.setOption(
        {
          color: ["#1E6BB8", "#D97706"],
          textStyle: chartFont,
          legend: { show: false },
          grid: {
            left: 36,
            right: 40,
            top: 10,
            bottom: 24,
            containLabel: false,
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross", crossStyle: { color: "#45b8f0" } },
            textStyle: {
              fontFamily: chartFont.fontFamily,
              fontWeight: "bold",
              color: "#d7efff",
              backgroundColor: "rgba(4, 20, 36, .96)",
              borderColor: "rgba(61, 175, 230, .55)",
            },
            formatter: (params) => {
              if (!Array.isArray(params) || !params.length) return "";
              const lines = [params[0].axisValue];
              params.forEach((p) => {
                if (p.seriesName === "预估收益") {
                  lines.push(
                    `${p.marker}${p.seriesName} ${this.formatMoney(p.value)} 元`
                  );
                } else {
                  lines.push(
                    `${p.marker}${p.seriesName} ${this.formatCount(p.value)}`
                  );
                }
              });
              return lines.join("<br/>");
            },
          },
          xAxis: {
            type: "category",
            boundaryGap: false,
            data: xData,
            axisLine: { lineStyle: { color: "rgba(55, 132, 181, 0.45)" } },
            axisTick: { show: false },
            axisLabel: {
              ...chartFont,
              fontSize: 11,
              interval: labelInterval,
              hideOverlap: true,
            },
          },
          yAxis: [
            {
              type: "value",
              name: "",
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: {
                ...chartFont,
                fontSize: 11,
                formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
              },
              splitLine: {
                lineStyle: {
                  color: "rgba(55, 132, 181, 0.22)",
                  type: "dashed",
                },
              },
            },
            {
              type: "value",
              name: "",
              axisLine: { show: false },
              axisTick: { show: false },
              axisLabel: {
                ...chartFont,
                fontSize: 11,
                formatter: (v) => {
                  if (v >= 10000) return `${(v / 10000).toFixed(1)}w`;
                  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
                  return v;
                },
              },
              splitLine: { show: false },
            },
          ],
          series: [
            {
              name: "预估收益",
              type: "line",
              smooth: true,
              showSymbol: false,
              yAxisIndex: 0,
              data: revenueData,
              lineStyle: { width: 2.5 },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "rgba(30, 107, 184, 0.35)" },
                  { offset: 1, color: "rgba(30, 107, 184, 0.04)" },
                ]),
              },
              silent: !hasData,
            },
            {
              name: "有效曝光",
              type: "line",
              smooth: true,
              showSymbol: false,
              yAxisIndex: 1,
              data: impressionData,
              lineStyle: { width: 2.5 },
              silent: !hasData,
            },
          ],
        },
        true
      );
    },
    async loadChannelRevenue() {
      const filterKey = this.dateFilterKey;
      const cached = this.peekHistory("channelRevenue", filterKey);
      if (cached !== undefined) {
        this.totalRevenue = cached.totalRevenue;
        this.channelItems = cached.channelItems;
        this.renderChannelChart();
        return;
      }
      try {
        const [startTime, endTime] = this.dateRange;
        const summary = await getAdPlatformSummary(startTime, endTime);
        if (!this.stillOn(filterKey)) return;
        this.totalRevenue = Number(summary.totalRevenue) || 0;
        this.revenueOdometerTick += 1;
        const items = Array.isArray(summary.items) ? summary.items : [];
        const byName = {};
        items.forEach((item) => {
          if (item && item.adnName) byName[item.adnName] = item;
        });
        this.channelItems = assignChannelColors(
          CHANNEL_ORDER.map((name) => {
            const row = byName[name];
            return {
              adnName: name,
              percent: row ? Number(row.percent) || 0 : 0,
              ecpm: row ? Number(row.ecpm) || 0 : 0,
            };
          })
        );
      } catch (e) {
        if (!this.stillOn(filterKey)) return;
        this.totalRevenue = 0;
        this.revenueOdometerTick += 1;
        this.channelItems = assignChannelColors(
          CHANNEL_ORDER.map((name) => ({
            adnName: name,
            percent: 0,
            ecpm: 0,
          }))
        );
      }
      this.putHistory(
        "channelRevenue",
        {
          totalRevenue: this.totalRevenue,
          channelItems: this.channelItems,
        },
        filterKey
      );
      this.renderChannelChart();
    },
    renderChannelChart() {
      if (!this.channelChart) return;
      const hasValue = this.channelItems.some(
        (i) => (Number(i.percent) || 0) > 0
      );
      const emptyTip = `${this.dateRangeLabel}暂无渠道收益`;
      const data = hasValue
        ? this.channelItems
            .filter((item) => (Number(item.percent) || 0) > 0)
            .map((item) => ({
              name: item.adnName,
              value: Number(item.percent) || 0,
              itemStyle: { color: item.color },
            }))
        : [
            {
              name: "暂无数据",
              value: 1,
              itemStyle: { color: "#163a57" },
            },
          ];
      const split = hasValue && data.length > 1;
      const padAngle = split ? 7 : 0;
      const borderRadius = split ? 10 : 0;
      const depthData = data.map((item) => ({
        name: item.name,
        value: item.value,
        itemStyle: { color: "rgba(4, 16, 34, 0.55)" },
      }));

      if (this._channelHighlightHandlers && this.channelChart) {
        this.channelChart.off("mouseover", this._channelHighlightHandlers.over);
        this.channelChart.off("globalout", this._channelHighlightHandlers.out);
        this._channelHighlightHandlers = null;
      }

      this.channelChart.setOption(
        {
          tooltip: {
            trigger: "item",
            formatter: (p) => {
              if (!hasValue) return emptyTip;
              return `${p.name}<br/>占比 ${this.formatPercent(p.percent)}%`;
            },
          },
          series: [
            {
              type: "pie",
              radius: ["46%", "76%"],
              center: ["50%", "54%"],
              silent: true,
              z: 1,
              padAngle,
              startAngle: 90,
              clockwise: true,
              label: { show: false },
              labelLine: { show: false },
              data: depthData,
              itemStyle: { borderRadius, borderWidth: 0 },
              animation: false,
            },
            {
              type: "pie",
              radius: ["46%", "76%"],
              center: ["50%", "50%"],
              z: 2,
              padAngle,
              startAngle: 90,
              clockwise: true,
              avoidLabelOverlap: true,
              label: { show: false },
              labelLine: { show: false },
              data,
              silent: !hasValue,
              itemStyle: {
                borderRadius,
                borderWidth: 0,
                shadowBlur: 10,
                shadowColor: "rgba(0, 0, 0, 0.28)",
              },
              emphasis: {
                scale: true,
                scaleSize: 4,
              },
            },
          ],
        },
        true
      );
    },
    async loadMonthlyNewUsers() {
      try {
        const res = await get("/admin/dashboard/monthlyNewUsers");
        this.monthlyNewUsers = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.monthlyNewUsers = [];
      }
      this.renderMonthChart();
    },
    async loadRiskCenter() {
      const filterKey = this.dateFilterKey;
      const cached = this.peekHistory("riskCenter", filterKey);
      if (cached !== undefined) {
        this.riskDeviceCount = cached.riskDeviceCount;
        this.warnDeviceCount = cached.warnDeviceCount;
        this.aliyunDetectCount = cached.aliyunDetectCount;
        this.riskRatio = cached.riskRatio;
        this.riskTypeStats = cached.riskTypeStats;
        this.riskRecords = cached.riskRecords;
        this.renderRiskRadarChart();
        return;
      }
      try {
        const [startTime, endTime] = this.dateRange;
        const data = await getRiskCenter(24, startTime, endTime);
        if (!this.stillOn(filterKey)) return;
        this.riskDeviceCount = data.riskDeviceCount || 0;
        this.warnDeviceCount = data.warnDeviceCount || 0;
        this.aliyunDetectCount = data.aliyunDetectCount || 0;
        this.riskRatio = data.riskRatio || 0;
        this.riskTypeStats = Array.isArray(data.typeStats)
          ? data.typeStats
          : [];
        this.riskRecords = Array.isArray(data.records) ? data.records : [];
      } catch (e) {
        if (!this.stillOn(filterKey)) return;
        this.riskDeviceCount = 0;
        this.warnDeviceCount = 0;
        this.aliyunDetectCount = 0;
        this.riskRatio = 0;
        this.riskTypeStats = [];
        this.riskRecords = [];
      }
      this.putHistory(
        "riskCenter",
        {
          riskDeviceCount: this.riskDeviceCount,
          warnDeviceCount: this.warnDeviceCount,
          aliyunDetectCount: this.aliyunDetectCount,
          riskRatio: this.riskRatio,
          riskTypeStats: this.riskTypeStats,
          riskRecords: this.riskRecords,
        },
        filterKey
      );
      this.renderRiskRadarChart();
    },
    /** 实时在线人数（不受日期筛选影响） */
    async loadOnlineUsers() {
      try {
        const res = await get("/admin/dashboard/onlineUsers");
        this.onlineCount = res.data || 0;
      } catch (e) {
        this.onlineCount = 0;
      }
    },
    /** 省份活跃用户 + 收益（JOIN 聚合，跟随日期筛选） */
    async loadUserLocations() {
      const emptyPoints = () =>
        PROVINCE_POINTS.map(([name, x, y]) => ({
          name,
          x,
          y,
          userCount: 0,
          revenue: 0,
        }));
      const filterKey = this.dateFilterKey;
      const cached = this.peekHistory("provincePoints", filterKey);
      if (cached !== undefined) {
        this.provincePoints = cached;
        return;
      }
      try {
        const [startTime, endTime] = this.dateRange;
        const rows = await getUserLocations(startTime, endTime);
        if (!this.stillOn(filterKey)) return;
        const byProvince = {};
        rows.forEach((item) => {
          if (!item.province) return;
          byProvince[item.province] = {
            userCount: item.userCount || 0,
            revenue: item.revenue || 0,
          };
        });
        this.provincePoints = PROVINCE_POINTS.map(([name, x, y]) => {
          const row = byProvince[name] || {};
          return {
            name,
            x,
            y,
            userCount: row.userCount || 0,
            revenue: row.revenue || 0,
          };
        });
      } catch (e) {
        if (!this.stillOn(filterKey)) return;
        this.provincePoints = emptyPoints();
      }
      this.putHistory("provincePoints", this.provincePoints, filterKey);
    },
    renderMonthChart() {
      if (!this.monthChart) return;
      const data = this.monthlyNewUsers;
      const hasData = data && data.length > 0;
      const xData = hasData
        ? data.map((i) => {
            const parts = String(i.month || "").split("-");
            return parts.length >= 2 ? `${parseInt(parts[1], 10)}月` : i.month;
          })
        : ["1月", "2月", "3月", "4月", "5月", "6月"];
      const seriesData = hasData
        ? data.map((i) => Number(i.count) || 0)
        : [0, 0, 0, 0, 0, 0];
      const maxVal = Math.max(...seriesData, 0);
      const yMax = maxVal > 0 ? Math.ceil(maxVal * 1.25) : 10;

      this.monthChart.setOption(
        {
          grid: { left: 4, right: 4, top: 18, bottom: 2, containLabel: true },
          xAxis: {
            type: "category",
            data: xData,
            axisLine: { lineStyle: { color: "rgba(55, 132, 181, 0.4)" } },
            axisTick: { show: false },
            axisLabel: {
              color: "#78a4c1",
              fontSize: 10,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            max: yMax,
            splitNumber: 4,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              show: true,
              color: "#6a8aae",
              fontSize: 10,
              margin: 6,
              formatter: (v) => {
                if (v >= 10000)
                  return `${(v / 10000).toFixed(v % 10000 === 0 ? 0 : 1)}万`;
                if (v >= 1000)
                  return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
                return v;
              },
            },
            splitLine: {
              show: true,
              lineStyle: { color: "rgba(150, 175, 205, 0.35)", type: "dashed" },
            },
          },
          series: [
            {
              type: "bar",
              data: seriesData,
              barWidth: "42%",
              label: {
                show: true,
                position: "top",
                color: "#43c9ff",
                fontSize: 10,
                fontWeight: 600,
                formatter: (p) => (p.value > 0 ? p.value : ""),
              },
              itemStyle: {
                borderRadius: [3, 3, 0, 0],
                color: "#159fe5",
              },
            },
          ],
        },
        true
      );
    },
  },
};
</script>

<style scoped>
.home-page {
  height: 100%;
  width: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 0 18px 18px;
  color: #d8edff;
  background: radial-gradient(
    ellipse at 50% 45%,
    #0b2543 0%,
    #061426 55%,
    #030b16 100%
  );
  font-family: "DIN Alternate", "Bahnschrift", "Microsoft YaHei", sans-serif;
  overflow: hidden;
}
.cockpit-header {
  flex: none;
  height: clamp(78px, 6.9vw, 22px);
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 -18px 12px;
  padding: 0 22px;
  background-color: transparent;
  background-position: center top;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}
.cockpit-header h1 {
  position: relative;
  z-index: 1;
  margin: 0;
  padding-bottom: 2px;
  font-size: clamp(16px, 1.35vw, 22px);
  line-height: 1;
  letter-spacing: 5px;
  color: #e9f7ff;
  text-shadow: 0 0 18px rgba(38, 173, 255, 0.65);
  font-weight: 650;
}
/* .header-side {
  position: absolute;
  left: 22px;
  top: 36%;
  transform: translateY(-72%);
  z-index: 1;
  color: #4386bb;
  font-size: 10px;
  letter-spacing: 2px;
} */
.live-indicator {
  display: inline-block;
  width: 7px;
  height: 7px;
  background: #28ddc2;
  border-radius: 50%;
  margin-right: 8px;
  box-shadow: 0 0 10px #28ddc2;
  vertical-align: 1px;
}
.cockpit-clock {
  position: absolute;
  right: 22px;
  top: 36%;
  transform: translateY(-72%);
  z-index: 1;
  display: flex;
  gap: 12px;
  align-items: center;
  color: #56cffa;
  font-size: 18px;
  letter-spacing: 1px;
}
.cockpit-clock strong {
  font-size: 18px;
  color: #56cffa;
  font-weight: 600;
  letter-spacing: 2px;
}
.fullscreen-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(57, 151, 203, 0.35);
  border-radius: 3px;
  background: rgba(7, 28, 54, 0.35);
  color: #81ddff;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
}
.fullscreen-btn svg {
  width: 14px;
  height: 14px;
  display: block;
}
.fullscreen-btn:hover {
  background: rgba(31, 155, 213, 0.2);
  border-color: rgba(71, 216, 255, 0.55);
}
.range-switch {
  display: flex;
  border: 1px solid rgba(57, 151, 203, 0.35);
  border-radius: 3px;
  overflow: hidden;
}
.range-switch button {
  border: 0;
  border-right: 1px solid rgba(57, 151, 203, 0.25);
  padding: 4px 9px;
  background: transparent;
  color: #608cac;
  font-size: 11px;
  cursor: pointer;
}
.range-switch button:last-child {
  border-right: 0;
}
.range-switch button.active {
  background: rgba(31, 155, 213, 0.2);
  color: #81ddff;
}
.cockpit-grid {
  position: relative;
  flex: 1;
  height: auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(300px, 1fr) minmax(480px, 1.82fr) minmax(
      300px,
      1fr
    );
  gap: 12px;
  overflow: visible;
}
.side-stack {
  position: relative;
  z-index: 3;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10px;
}
.left-stack {
  grid-template-columns: 1fr;
  grid-template-rows: minmax(0, 1.05fr) minmax(0, 0.82fr) minmax(0, 1.55fr);
}
.left-stack .card-channel {
  grid-column: auto;
}
.right-stack {
  grid-template-columns: 1fr;
  grid-template-rows: 1.45fr 0.95fr;
}
.dash-card {
  position: relative;
  z-index: 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  padding: 12px 13px 10px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  box-shadow: none;
}
.dash-card:before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background-color: rgba(7, 28, 54, 0.55);
  background-image: var(--card-bg);
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  opacity: 0.76;
  pointer-events: none;
}
.dash-card:after {
  display: none;
}
.card-title {
  box-sizing: border-box;
  width: 100%;
  height: 36px;
  margin: 0 0 4px;
  padding: 0 28px 10px 44px;
  display: flex;
  align-items: center;
  color: #e8f7ff;
  font-size: 13px;
  line-height: 1;
  letter-spacing: 0.5px;
  font-weight: 600;
  white-space: nowrap;
  background-image: var(--level-bg);
  /* 图片下半约 40% 是过渡光晕，标题只占实心条，光晕伸进内容区 */
  background-position: left top;
  background-size: 100% 155%;
  background-repeat: no-repeat;
  text-shadow: 0 0 10px rgba(40, 170, 255, 0.45);
}
.card-body {
  height: calc(100% - 40px);
  min-height: 0;
}
.channel-body {
  height: calc(100% - 40px);
}
.channel-main {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.channel-chart {
  width: 56%;
  height: 100%;
  min-width: 68px;
}
.channel-legend {
  width: 44%;
  padding: 0;
  list-style: none;
}
.channel-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0;
  color: #9ec8e4;
  font-size: 11px;
  white-space: nowrap;
}
.channel-legend-item .dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}
.channel-legend-item .name {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}
.channel-legend-item .pct {
  color: #d9f4ff;
  font-family: monospace;
}
.ecpm-body {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 6px;
  padding: 4px 6px 6px;
}
.ecpm-row {
  display: grid;
  grid-template-columns: 56px 1fr 42px;
  align-items: center;
  gap: 8px;
  min-height: 0;
}
.ecpm-name {
  color: #8fb4cf;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ecpm-track {
  height: 8px;
  border-radius: 4px;
  background: rgba(20, 52, 82, 0.85);
  overflow: hidden;
}
.ecpm-fill {
  position: relative;
  height: 100%;
  border-radius: 4px;
  box-shadow: 0 0 10px currentColor;
  transition: width 0.4s ease;
}
.ecpm-fill::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.38) 0%,
    rgba(255, 255, 255, 0) 58%
  );
  pointer-events: none;
}
.ecpm-val {
  color: #d9f4ff;
  font-size: 11px;
  font-family: "DIN Alternate", monospace;
  text-align: right;
  white-space: nowrap;
}
.card-warn {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.risk-header {
  position: relative;
  margin-bottom: 8px;
}
.risk-header .card-title {
  margin-bottom: 0;
  padding-right: 140px;
}
.risk-total {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-55%);
  z-index: 1;
  font-size: 9px;
  color: #769cb8;
  white-space: nowrap;
}
.risk-total b {
  font-size: 14px;
  color: #ff8b61;
  margin-left: 3px;
}
.risk-mini-stats {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #7396b2;
  padding: 2px 0 5px;
}
.risk-mini-stats b {
  color: #47d9ff;
  margin-left: 3px;
}
.risk-body {
  flex: 1;
  display: grid;
  grid-template-rows: minmax(120px, 34%) minmax(0, 1fr);
  height: auto !important;
  margin-top: 0;
  min-height: 0;
  overflow: hidden;
}
.risk-radar-pane {
  min-height: 0;
  overflow: hidden;
}
.risk-radar-chart {
  width: 100%;
  height: 100%;
}
.risk-table-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-top: 1px solid rgba(58, 134, 183, 0.23);
  overflow: hidden;
}
.risk-empty {
  height: 100%;
  display: grid;
  place-items: center;
  color: #b7d7ea;
  font-size: 11px;
}
.risk-scroll-viewport {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.risk-scroll-track.is-scrolling {
  animation-name: riskMarquee;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: transform;
}
.risk-scroll-viewport:hover .risk-scroll-track.is-scrolling {
  animation-play-state: paused;
}
@keyframes riskMarquee {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}
.risk-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  color: #dff4ff;
  font-size: 11px;
}
.risk-table-head {
  flex: none;
}
.risk-table th {
  color: #8fe4ff;
  font-weight: 500;
}
.risk-table-body td {
  color: #e7f7ff;
}
.risk-table th,
.risk-table td {
  padding: 4px 2px;
  border-bottom: 1px solid rgba(66, 128, 167, 0.18);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.risk-table th:nth-child(1),
.risk-table td:nth-child(1) {
  width: 24%;
}
.risk-table th:nth-child(2),
.risk-table td:nth-child(2) {
  width: 22%;
}
.risk-table th:nth-child(3),
.risk-table td:nth-child(3) {
  width: 34%;
}
.risk-table th:nth-child(4),
.risk-table td:nth-child(4) {
  width: 20%;
}
.map-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
}
.map-card.is-map-zoomed {
  z-index: 6;
}
.map-card.is-map-zoomed .china-map-wrap {
  z-index: 6;
  pointer-events: auto;
}
.map-card.is-map-zoomed .province-map-layer {
  overflow: visible;
  z-index: 6;
}
.map-card:after {
  display: none;
}
.map-card:before {
  display: block;
  content: "";
  position: absolute;
  inset: auto;
  left: 50%;
  top: -52px;
  z-index: 0;
  width: 156%;
  height: calc(118% + 52px);
  transform: translateX(-50%);
  background-color: transparent;
  background-image: var(--map-area-bg);
  background-position: center top;
  background-size: contain;
  background-repeat: no-repeat;
  opacity: 1;
  pointer-events: none;
}
.map-kpis {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: clamp(28px, 7vw, 100px);
  flex-shrink: 0;
  padding: 0 0 2px;
}
.map-kpi {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 150px;
}
.map-kpi span {
  font-size: 12px;
  letter-spacing: 2px;
  color: #71aacc;
}
.map-kpi strong {
  color: #47d8ff;
  font-size: 32px;
  line-height: 1.2;
  letter-spacing: 1px;
  text-shadow: 0 0 15px rgba(42, 194, 255, 0.45);
  font-family: "DIN Alternate", monospace;
}
.map-kpi.revenue strong {
  color: #ffe38a;
  text-shadow: 0 0 15px rgba(255, 196, 80, 0.32);
}
.map-kpi.online strong {
  color: #43e0bd;
  text-shadow: 0 0 15px rgba(67, 224, 189, 0.4);
}
.map-kpi small {
  font-size: 8px;
  letter-spacing: 2px;
  color: #41769b;
}
.china-map-wrap {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: visible;
  pointer-events: none;
  margin-top: -18px;
  margin-bottom: -72px;
}
.province-map-layer {
  position: relative;
  z-index: 1;
  flex: none;
  box-sizing: border-box;
  width: min(100%, 72vh);
  max-width: 100%;
  aspect-ratio: 2511 / 1632;
  max-height: none;
  pointer-events: auto;
}
.map-bg2-layer {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 0;
  width: 100%;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  background-image: var(--map-area-bg2);
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  pointer-events: none;
}
.province-map-layer .china-province-map {
  position: relative;
  z-index: 1;
}
.map-revenue-panel {
  position: relative;
  flex: 0 0 clamp(250px, 34vh, 320px);
  min-height: 250px;
  max-height: none;
  margin: 0 2px 2px;
  z-index: 2;
  overflow: visible;
  background: transparent;
}
.map-revenue-panel:before {
  opacity: 0.82;
  background-color: rgba(5, 20, 40, 0.55);
}
.map-revenue-panel .card-title {
  width: max-content;
  max-width: 100%;
  height: 34px;
  margin-bottom: 0;
  padding: 0 34px 8px 28px;
  background-size: 100% 155%;
  background-position: left top;
}
.map-revenue-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 2px;
  min-height: 34px;
}
.trend-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding-right: 6px;
}
.trend-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #8fb8d4;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.trend-legend-item i {
  display: block;
  width: 16px;
  height: 3px;
  border-radius: 2px;
}
.trend-legend-item.is-revenue i {
  background: #1e6bb8;
  box-shadow: 0 0 6px rgba(30, 107, 184, 0.7);
}
.trend-legend-item.is-impression i {
  background: #d97706;
  box-shadow: 0 0 6px rgba(217, 119, 6, 0.7);
}
.map-revenue-panel .card-body {
  min-height: 190px;
}
.map-note {
  position: absolute;
  right: 5px;
  bottom: 5px;
  color: #4a7898;
  font-size: 9px;
}
.metric-grid {
  min-width: 0;
  min-height: 0;
  height: calc(100% - 40px);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px 0;
}
.metric-cube {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 2px;
  overflow: hidden;
}
.metric-cube-value {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
  line-height: 1;
  color: var(--kpi-color);
  text-shadow: 0 0 14px var(--kpi-glow);
}
.metric-cube-value strong {
  font-size: clamp(15px, 1.25vw, 22px);
  font-weight: 800;
  font-family: "DIN Alternate", "Bahnschrift", sans-serif;
}
.metric-cube-value small {
  font-size: clamp(10px, 0.85vw, 13px);
  font-weight: 700;
}
.metric-cube-label {
  position: relative;
  z-index: 2;
  margin-top: 3px;
  margin-bottom: -4px;
  color: #e8f4ff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
  text-shadow: 0 0 8px rgba(40, 140, 210, 0.35);
}
.metric-cube-stage {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 58px;
  flex: none;
  margin-top: -2px;
}
.metric-cube-css {
  position: absolute;
  left: 50%;
  bottom: 4%;
  width: 86px;
  height: 64px;
  transform: translateX(-50%);
  filter: drop-shadow(0 8px 10px var(--kpi-glow));
}
.metric-cube-css .face {
  position: absolute;
  inset: 0;
  display: block;
}
.metric-cube-css .top {
  clip-path: polygon(50% 6%, 94% 34%, 50% 56%, 6% 34%);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5),
    var(--kpi-top) 55%,
    rgba(0, 0, 0, 0.12)
  );
}
.metric-cube-css .left {
  clip-path: polygon(6% 34%, 50% 56%, 50% 94%, 6% 70%);
  background: linear-gradient(180deg, var(--kpi-left), rgba(0, 0, 0, 0.38));
}
.metric-cube-css .right {
  clip-path: polygon(94% 34%, 50% 56%, 50% 94%, 94% 70%);
  background: linear-gradient(180deg, var(--kpi-right), rgba(0, 0, 0, 0.5));
}
.metric-cube-img {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 92px;
  height: 68px;
  transform: translate(-50%, -42%);
  object-fit: contain;
  object-position: center;
  opacity: 0;
  pointer-events: none;
}
.metric-cube-img.ready {
  opacity: 1;
}
.metric-cube.is-orange {
  --kpi-color: #ff7a2a;
  --kpi-glow: rgba(255, 122, 42, 0.55);
  --kpi-top: rgba(255, 168, 92, 0.72);
  --kpi-left: rgba(255, 110, 32, 0.62);
  --kpi-right: rgba(196, 62, 12, 0.58);
}
.metric-cube.is-gold {
  --kpi-color: #e8c43a;
  --kpi-glow: rgba(232, 196, 58, 0.5);
  --kpi-top: rgba(255, 226, 120, 0.72);
  --kpi-left: rgba(214, 176, 36, 0.62);
  --kpi-right: rgba(164, 122, 8, 0.58);
}
.metric-cube.is-purple {
  --kpi-color: #9b5cff;
  --kpi-glow: rgba(155, 92, 255, 0.52);
  --kpi-top: rgba(196, 158, 255, 0.72);
  --kpi-left: rgba(139, 78, 236, 0.62);
  --kpi-right: rgba(86, 36, 176, 0.58);
}
.metric-cube.is-green {
  --kpi-color: #2fd06a;
  --kpi-glow: rgba(47, 208, 106, 0.5);
  --kpi-top: rgba(120, 236, 164, 0.7);
  --kpi-left: rgba(36, 186, 88, 0.62);
  --kpi-right: rgba(12, 122, 56, 0.58);
}
.metric-cube.is-cyan {
  --kpi-color: #2ec8d4;
  --kpi-glow: rgba(46, 200, 212, 0.5);
  --kpi-top: rgba(132, 236, 244, 0.7);
  --kpi-left: rgba(28, 176, 188, 0.62);
  --kpi-right: rgba(8, 112, 128, 0.58);
}
.metric-cube.is-blue {
  --kpi-color: #3b82f6;
  --kpi-glow: rgba(59, 130, 246, 0.52);
  --kpi-top: rgba(130, 176, 255, 0.72);
  --kpi-left: rgba(48, 112, 232, 0.62);
  --kpi-right: rgba(20, 64, 168, 0.58);
}
.trend-chart {
  height: 100%;
  width: 100%;
  min-width: 0;
}
.month-panel .card-body {
  height: calc(100% - 40px);
}
.month-chart {
  height: 100%;
  width: 100%;
}
@media (max-width: 1200px) {
  .home-page {
    overflow: auto;
  }
  .cockpit-grid {
    height: auto;
    min-height: 0;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: minmax(420px, 56vh) minmax(360px, 48vh);
  }
  .map-card {
    grid-column: 1 / -1;
    grid-row: 1;
  }
  .side-stack {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
  }
  .left-stack {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(180px, 1fr) minmax(140px, 0.8fr) minmax(
        240px,
        1.5fr
      );
    grid-column: 1;
    grid-row: 2;
  }
  .right-stack {
    grid-template-columns: 1fr;
    grid-template-rows: 1.4fr 0.95fr;
    grid-column: 2;
    grid-row: 2;
  }
  .cockpit-header {
    position: sticky;
    top: 0;
    background-color: #061426;
    z-index: 8;
  }
  .map-kpis {
    gap: 35px;
  }
}
@media (max-width: 720px) {
  .home-page {
    padding: 0 8px 8px;
    overflow: auto;
  }
  .cockpit-header {
    height: 68px;
    margin: 0 -8px 10px;
    padding: 0 12px;
  }
  .cockpit-header h1 {
    font-size: 17px;
    letter-spacing: 2px;
    padding-bottom: 2px;
  }
  /* .header-side {
    display: none;
  } */
  .cockpit-clock {
    right: 12px;
    gap: 5px;
    font-size: 9px;
  }
  .fullscreen-btn {
    height: 24px;
    padding: 0 8px;
    font-size: 11px;
  }
  .fullscreen-btn span {
    display: none;
  }
  .cockpit-clock strong {
    font-size: 14px;
  }
  .range-switch {
    display: none;
  }
  .cockpit-grid {
    display: flex;
    flex-direction: column;
    height: auto;
  }
  .map-card {
    height: auto;
    min-height: 420px;
    order: 0;
  }
  .china-map-wrap {
    min-height: 280px;
    margin-top: -8px;
  }
  .map-revenue-panel {
    flex: 0 0 260px;
    min-height: 260px;
  }
  .side-stack {
    min-height: 640px;
    order: 1;
  }
  .right-stack {
    min-height: 560px;
  }
  .map-kpi {
    min-width: 110px;
  }
  .map-kpi strong {
    font-size: 24px;
  }
  .dash-card {
    padding: 10px;
  }
  .risk-table {
    font-size: 8px;
  }
}
</style>
